// CRM → Gestión Comercial → Prospectos (/crm/prospectos).
// El prospecto ES un financiero.partner con estado_partner='prospecto': crear
// aquí crea el partner (código PRO-#### automático) y "Convertir" lo vuelve
// cliente activo sin duplicar maestros. RPCs del esquema crm (script 315).
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRightCircle, Pencil, Plus, ShieldCheck, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SelectPais } from '@/components/ui/SelectPais';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, SELECT_PLACEHOLDER } from '@/features/admin/shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { ExportarBotones } from '@/lib/utils/exportarTabla';
import { ArcoTitular } from './ArcoTitular';
import type { Celda } from '@/lib/utils/exportarTabla';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';

const CRM = { headers: { 'Content-Profile': 'crm', 'Accept-Profile': 'crm' } };

interface Prospecto {
  id: string; codigo: string | null; razon_social: string; nombre_comercial: string | null;
  tipo_identificacion: string | null; identificacion: string | null; email: string | null;
  telefono: string | null; celular: string | null; industria: string | null; origen: string | null;
  ciudad: string | null; provincia: string | null; pais: string | null;
  direccion: string | null; sitio_web: string | null;
  notas: string | null; fecha_primer_contacto: string | null; estado_partner: string;
  created_at: string; vendedor_id: string | null; vendedor: string | null; contratos: number;
}
// El vendedor es un USUARIO del sistema, no un partner: es quien inicia sesión
// y a quien se le asigna la bandeja de conversaciones (script 842).
interface VendedorOpt { id: string; username: string; nombre: string; email: string | null }

const ORIGENES = ['referido', 'web', 'redes sociales', 'feria/evento', 'llamada', 'visita', 'otro'];

const FORM0 = {
  razon_social: '', nombre_comercial: '', tipo_identificacion: 'CEDULA', identificacion: '',
  email: '', telefono: '', celular: '', sitio_web: '', industria: '', origen: '',
  direccion: '', ciudad: '', provincia: '', pais: 'Ecuador', notas: '', vendedor_id: '', fecha_primer_contacto: '',
};

export function Prospectos() {
  const qc = useQueryClient();
  const [festado, setFestado] = useState<'prospecto' | 'todos'>('prospecto');
  const [busqueda, setBusqueda] = useState('');
  const [vista, setVista] = useState<'lista' | 'form'>('lista');
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...FORM0 });
  // Titular sobre el que se están ejerciendo los derechos ARCO (script 843).
  const [arco, setArco] = useState<{ id: string; nombre: string } | null>(null);
  const set = (k: keyof typeof FORM0, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const dlg = useDialogoEnLinea();

  const { data: prospectos = [] } = useQuery<Prospecto[]>({
    queryKey: ['crm-prospectos', festado],
    queryFn: async () => (await apiClient.post('/rpc/crm_prospectos_listar', { _estado: festado }, CRM)).data,
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: vendedores = [] } = useQuery<VendedorOpt[]>({
    queryKey: ['crm-vendedores'],
    queryFn: async () => (await apiClient.post('/rpc/crm_vendedores', {}, CRM)).data || [],
  });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['crm-prospectos'] });
    qc.invalidateQueries({ queryKey: ['crm-vendedores'] });
  };

  const guardar = useMutation({
    mutationFn: async () =>
      (await apiClient.post('/rpc/crm_prospecto_guardar', { _data: form, _id: editId }, CRM)).data,
    onSuccess: () => {
      invalidar(); setVista('lista');
      toast.success(editId ? 'Prospecto actualizado' : 'Prospecto creado (partner incluido)');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const convertir = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post('/rpc/crm_prospecto_convertir', { _partner_id: id }, CRM)).data,
    onSuccess: () => { invalidar(); toast.success('Prospecto convertido a CLIENTE activo'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const abrirNuevo = () => { setEditId(null); setForm({ ...FORM0 }); setVista('form'); };
  const abrirEditar = (p: Prospecto) => {
    setEditId(p.id);
    setForm({
      razon_social: p.razon_social, nombre_comercial: p.nombre_comercial ?? '',
      tipo_identificacion: p.tipo_identificacion ?? 'CEDULA', identificacion: p.identificacion ?? '',
      email: p.email ?? '', telefono: p.telefono ?? '', celular: p.celular ?? '',
      sitio_web: p.sitio_web ?? '', industria: p.industria ?? '', origen: p.origen ?? '',
      direccion: p.direccion ?? '', ciudad: p.ciudad ?? '', provincia: p.provincia ?? '',
      // Sin esto, editar cualquier campo de un prospecto de otro país le
      // reescribiría el país a «Ecuador» por el valor por defecto del formulario.
      pais: p.pais ?? 'Ecuador',
      notas: p.notas ?? '', vendedor_id: p.vendedor_id ?? '',
      fecha_primer_contacto: p.fecha_primer_contacto?.slice(0, 10) ?? '',
    });
    setVista('form');
  };

  const filtrados = useMemo(() => prospectos.filter((p) => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return `${p.razon_social} ${p.identificacion ?? ''} ${p.codigo ?? ''} ${p.email ?? ''} ${p.origen ?? ''}`
      .toLowerCase().includes(q);
  }), [prospectos, busqueda]);

  const getVal = useCallback((p: Prospecto, key: string): string | number | null => {
    switch (key) {
      case 'codigo': return p.codigo;
      case 'razon_social': return p.razon_social;
      case 'identificacion': return p.identificacion;
      case 'contacto': return p.email ?? p.telefono ?? p.celular;
      case 'origen': return p.origen;
      case 'industria': return p.industria;
      case 'vendedor': return p.vendedor;
      case 'primer_contacto': return p.fecha_primer_contacto;
      case 'contratos': return Number(p.contratos);
      case 'estado': return p.estado_partner;
      default: return null;
    }
  }, []);
  const sort = useSort(filtrados, getVal);

  const headersExp = ['N°', 'Código', 'Razón social', 'Identificación', 'Email', 'Teléfono',
    'Origen', 'Industria', 'Vendedor', '1er contacto', 'Contratos', 'Estado'];
  const filasExp: Celda[][] = sort.sorted.map((p, i) => [
    i + 1, p.codigo, p.razon_social, p.identificacion, p.email, p.telefono ?? p.celular,
    p.origen, p.industria, p.vendedor, p.fecha_primer_contacto?.slice(0, 10) ?? null,
    Number(p.contratos), p.estado_partner === 'prospecto' ? 'Prospecto' : 'Cliente',
  ]);

  if (vista === 'form') {
    return (
      <div className="p-6 space-y-4 max-w-5xl">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => setVista('lista')} title="Volver al listado">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <UserPlus className="w-5 h-5" /> {editId ? 'Editar prospecto' : 'Nuevo prospecto'}
          </h1>
        </div>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Datos del prospecto</CardTitle>
            {!editId && (
              <p className="text-xs text-muted-foreground">
                Se registrará también como partner (código PRO-#### automático); al convertirlo a cliente conserva todo su historial.
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              <Campo label="Razón social / Nombre *" className="md:col-span-2">
                <input className={fieldCls} value={form.razon_social} onChange={(e) => set('razon_social', e.target.value)} />
              </Campo>
              <Campo label="Nombre comercial">
                <input className={fieldCls} value={form.nombre_comercial} onChange={(e) => set('nombre_comercial', e.target.value)} />
              </Campo>
              <Campo label="Tipo identificación">
                <select className={fieldCls} value={form.tipo_identificacion} onChange={(e) => set('tipo_identificacion', e.target.value)}>
                  <option value="CEDULA">Cédula</option><option value="RUC">RUC</option><option value="PASAPORTE">Pasaporte</option>
                </select>
              </Campo>
              <Campo label="Identificación">
                <input className={fieldCls} maxLength={20} value={form.identificacion} onChange={(e) => set('identificacion', e.target.value)} />
              </Campo>
              <Campo label="Email">
                <input className={fieldCls} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
              </Campo>
              <Campo label="Teléfono">
                <input className={fieldCls} value={form.telefono} onChange={(e) => set('telefono', e.target.value)} />
              </Campo>
              <Campo label="Celular">
                <input className={fieldCls} value={form.celular} onChange={(e) => set('celular', e.target.value)} />
              </Campo>
              <Campo label="Sitio web">
                <input className={fieldCls} value={form.sitio_web} onChange={(e) => set('sitio_web', e.target.value)} />
              </Campo>
              <Campo label="Origen del contacto">
                <select className={fieldCls} value={form.origen} onChange={(e) => set('origen', e.target.value)}>
                  <option value="">{SELECT_PLACEHOLDER}</option>
                  {ORIGENES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Campo>
              <Campo label="Industria / actividad">
                <input className={fieldCls} value={form.industria} onChange={(e) => set('industria', e.target.value)} />
              </Campo>
              <Campo label="Vendedor asignado">
                <select className={fieldCls} value={form.vendedor_id} onChange={(e) => set('vendedor_id', e.target.value)}>
                  <option value="">{SELECT_PLACEHOLDER}</option>
                  {vendedores.map((v) => <option key={v.id} value={v.id}>{v.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Fecha primer contacto">
                <input className={fieldCls} type="date" value={form.fecha_primer_contacto}
                       onChange={(e) => set('fecha_primer_contacto', e.target.value)} />
              </Campo>
              <Campo label="Ciudad">
                <input className={fieldCls} value={form.ciudad} onChange={(e) => set('ciudad', e.target.value)} />
              </Campo>
              <Campo label="Provincia">
                <input className={fieldCls} value={form.provincia} onChange={(e) => set('provincia', e.target.value)} />
              </Campo>
              <Campo label="País">
                <SelectPais className={fieldCls} value={form.pais} onChange={(v) => set('pais', v)} />
              </Campo>
              <Campo label="Dirección" className="md:col-span-3">
                <input className={fieldCls} value={form.direccion} onChange={(e) => set('direccion', e.target.value)} />
              </Campo>
              <Campo label="Notas de seguimiento" className="md:col-span-3">
                <textarea className={fieldCls} rows={3} value={form.notas} onChange={(e) => set('notas', e.target.value)} />
              </Campo>
            </div>
            <div className="flex justify-end gap-2 mt-4 border-t pt-4">
              <Button variant="outline" onClick={() => setVista('lista')}>Cancelar</Button>
              <Button onClick={() => guardar.mutate()} isLoading={guardar.isPending}
                      disabled={!form.razon_social.trim()}>
                {editId ? 'Guardar cambios' : 'Crear prospecto'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <UserPlus className="w-5 h-5" /> Prospectos
        </h1>
        <div className="flex items-center gap-2">
          <ExportarBotones nombre="prospectos" titulo="Prospectos CRM" headers={headersExp}
            filas={filasExp} colsDerecha={[0, 10]} />
          <Button size="sm" onClick={abrirNuevo}><Plus className="w-4 h-4 mr-1" /> Nuevo prospecto</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3 flex-wrap">
            <CardTitle className="text-base">Clientes potenciales (integrados con Partner)</CardTitle>
            <select className={`${fieldCls} !w-auto`} value={festado}
                    onChange={(e) => setFestado(e.target.value as 'prospecto' | 'todos')}>
              <option value="prospecto">Solo prospectos</option>
              <option value="todos">Prospectos + convertidos</option>
            </select>
            <input className={`${fieldCls} !w-64`} placeholder="Buscar (nombre, cédula/RUC, origen…)"
                   value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="py-2 pr-3 text-right w-10">N°</th>
                  <SortTh col="codigo" sort={sort}>Código</SortTh>
                  <SortTh col="razon_social" sort={sort}>Razón social</SortTh>
                  <SortTh col="identificacion" sort={sort}>Identificación</SortTh>
                  <SortTh col="contacto" sort={sort}>Contacto</SortTh>
                  <SortTh col="origen" sort={sort}>Origen</SortTh>
                  <SortTh col="vendedor" sort={sort}>Vendedor</SortTh>
                  <SortTh col="primer_contacto" sort={sort}>1er contacto</SortTh>
                  <SortTh col="contratos" sort={sort} align="right">Contratos</SortTh>
                  <SortTh col="estado" sort={sort} align="center">Estado</SortTh>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((p, i) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-mono">{p.codigo}</td>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{p.razon_social}</div>
                      {p.nombre_comercial && <div className="text-xs text-muted-foreground">{p.nombre_comercial}</div>}
                    </td>
                    <td className="py-2 pr-3 font-mono">{p.identificacion ?? '—'}</td>
                    <td className="py-2 pr-3 text-xs">
                      {p.email && <div>{p.email}</div>}
                      <div className="text-muted-foreground">{p.celular ?? p.telefono ?? ''}</div>
                    </td>
                    <td className="py-2 pr-3 capitalize">{p.origen ?? '—'}</td>
                    <td className="py-2 pr-3">{p.vendedor ?? '—'}</td>
                    <td className="py-2 pr-3">{p.fecha_primer_contacto?.slice(0, 10) ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">{p.contratos}</td>
                    <td className="py-2 pr-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.estado_partner === 'prospecto'
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'}`}>
                        {p.estado_partner === 'prospecto' ? 'Prospecto' : 'Cliente'}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" title="Editar" onClick={() => abrirEditar(p)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" title="Datos personales (LOPDP)"
                              onClick={() => setArco({ id: p.id, nombre: p.razon_social })}>
                        <ShieldCheck className="w-4 h-4" />
                      </Button>
                      {p.estado_partner === 'prospecto' && (
                        <Button size="sm" variant="outline" disabled={convertir.isPending}
                                title="Convertir a cliente activo"
                                onClick={async () => { if (await dlg.confirmar(`¿Convertir a "${p.razon_social}" en CLIENTE activo?`)) convertir.mutate(p.id); }}>
                          <ArrowRightCircle className="w-4 h-4 mr-1" /> Convertir
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!sort.sorted.length && (
                  <tr><td colSpan={11} className="py-6 text-center text-muted-foreground">
                    Sin prospectos. Crea el primero con «Nuevo prospecto» — se registrará también como partner.
                  </td></tr>
                )}
                {sort.sorted.length > 0 && (
                  <tr className="border-t-2 font-semibold bg-muted/30">
                    <td className="py-2 pr-3" colSpan={8}>Totales ({sort.sorted.length})</td>
                    <td className="py-2 pr-3 text-right">{sort.sorted.reduce((a, p) => a + Number(p.contratos), 0)}</td>
                    <td colSpan={2} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {arco && (
        <ArcoTitular partnerId={arco.id} nombre={arco.nombre}
                     onClose={() => setArco(null)} onHecho={invalidar} />
      )}
    </div>
  );
}
